import React, { useState, useEffect, useContext } from 'react';
import {
  ANALYTICS_MESSAGES,
  SCREEN_NAMES,
} from 'common/utils/constants/AnalyticsConstants';
import { getAnalyticsBaseConfig } from 'common/utils';
import { GlobalContext } from 'context';
import { PASSWORD_CRITERIA } from '../forgot-password/PasswordConstant';
import {
  ModalCustomStyle,
  EmailRegex,
  getMaxLength,
  setStrengthBarColor,
  FormTitles,
  FormTitleTranslationKeys,
  handleKeyDownUsername,
  domainForSegment,
  getErrorTypeByCode,
  convertAPIRequestFormat,
  dateformat,
  setEncryptedValue,
  UserDetails,
} from 'common';
import { useTranslation } from 'react-i18next';
import PasswordInfoOverlay from './PasswordInfoOverlay';
import UsernameInfoOverlay from './UsernameInfoOverlay';
import Modal from 'react-modal';
import {
  useUserName,
  useUserNameValidation,
  useUserPassword,
  useProfileRegister,
  useGuestRegister,
} from '@sentara-health/sentara-api-hooks-core';
import {
  CloseIconBlock,
  ErrorMessage,
  Label,
  Button,
  InputField,
  GlobalError,
  Headings,
  Loader,
  HelpIcon,
  Checkbox,
  Link,
  Paragraph,
} from '@sentara-health/optimahealth_web';
import { useForm, FormProvider } from 'react-hook-form';
import {
  ChangePassword,
  ConfirmPassword,
  PasswordStrengthMeter,
} from 'templates';
import { useAdobeAnalytics } from '@sentara-health/platform-analytics';
import { useLocation } from 'react-router-dom';
import { AdobeFormConfig } from 'common/AdobeFormConfig';

interface FormData {
  [key: string]: string;
}

interface AccountSetupData {
  name: string;
  password: string;
  confirmPassword: string;
}

// Define types for your props and state
interface AccountSetupProps {
  formData: FormData[];
  page: number;
  setFormData: (response: any) => void;
  setPage: (response: any) => void;
  plantypes?: string;
  guest?: boolean;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
}
const AccountSetup: React.FC<AccountSetupProps> = (props) => {
  const { logging, ssoEndPoints, getFeature } = useContext(GlobalContext);
  const { formData, page, setFormData, setPage, guest } = props;
  const methods = useForm({
    mode: 'onBlur',
    defaultValues: {
      name: formData[page]?.name || '',
      password: formData[page]?.password || '',
      confirmPassword: formData[page]?.confirmPassword || '',
      emailID: formData[page]?.emailID ?? '',
      confirmEmailID: formData[page]?.confirmEmailID ?? '',
    },
  });
  const {
    handleSubmit,
    register,
    getValues,
    formState: { errors, isValid },
  } = methods;
  const { t } = useTranslation();
  const { RegisterUserName } = useUserName();
  const { RegisterUserNameValidation } = useUserNameValidation();
  const { userPassword } = useUserPassword();
  const [pwdStrength, setPwdStrength] = useState<number>(0);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] =
    useState<boolean>(false);
  const [checkBox, setCheckBox] = useState<boolean>(false);
  const [checkBoxError, setCheckBoxError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<boolean>(false);
  const [errorMessage1, setErrorMessage1] = useState<boolean>(false);
  const [errorPassword, setErrorPassword] = useState<boolean>(false);
  const [errorCode, setErrorCode] = useState<number>();
  const [value, setValue] = useState<any>('');
  const [loader, setLoader] = useState<boolean>(false);
  const { formSubmissionSucceeded, formSubmissionFailed } = useAdobeAnalytics();
  const { ProfileRegister } = useProfileRegister();
  const { GuestRegister } = useGuestRegister();
  const { pathname } = useLocation();

  const childOnlyPlanCheck = () => pathname.includes('ChildOnlyPlan');

  let errorUserName = t('activation.userName');

  function openModal(e: { preventDefault: () => void }) {
    e.preventDefault();
    setIsOpen(true);
  }

  function closeModal() {
    setIsOpen(false);
  }

  function closeModalPassword() {
    setIsPasswordModalOpen(false);
  }
  useEffect(() => {
    setStrengthBarColor(value, setPwdStrength);
  }, [value]);

  /** Terms and condition checkbox front validation  **/
  const checkboxValid = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setCheckBox(true);
      setCheckBoxError(false);
    } else {
      setCheckBox(false);
    }
  };

  /** Helper to build personal data payload */
  const buildPersonalData = (
    firstName: string,
    lastName: string,
    emailID: string,
    dob: string,
    ssn: string,
    name: string,
    password: string
  ) => ({
    firstName,
    lastName,
    emailId: emailID,
    dateOfBirth: convertAPIRequestFormat(dob, dateformat.dateFormatISO),
    ssn: ssn ?? '',
    userId: name,
    password,
  });

  /** Helper to build memberId data */
  const buildMemberIdData = () => {
    const medicaId =
      formData[FormTitles.indexOf('Personal Information')].medicaId ?? '';
    const medicareId =
      formData[FormTitles.indexOf('Personal Information')].medicareId ?? '';
    return { medicaId, medicareId };
  };

  /** Helper to get planId */
  const getPlanId = (): 'Medicaid' | 'Medicare' | 'individual' => {
    const planTypeValue =
      formData[FormTitles.indexOf('Personal Information')].planType;
    if (planTypeValue === 'Medicaid') {
      return 'Medicaid';
    } else if (planTypeValue === 'Medicare') {
      return 'Medicare';
    }
    return 'individual';
  };

  /** Helper to handle API result and errors */
  const handleApiResult = (result: any) => {
    if (result?.error) {
      formSubmissionFailed({
        segment: `${domainForSegment}${pathname}`,
        form: {
          ...AdobeFormConfig['accountSetupError'],
          formFailReason: result?.error ?? 'unknown',
        },
      });
    } else {
      formSubmissionSucceeded({
        segment: `${domainForSegment}${pathname}`,
        form: AdobeFormConfig['accountSetupSuccess'],
      });
    }

    if (result.errorCode) {
      setErrorCode(result.errorCode);
    }

    if (result?.data?.activationStatus === false) {
      logging?.trace({
        additionalDetails: getAnalyticsBaseConfig(),
        message: ANALYTICS_MESSAGES.REG_ACCOUNTSETUP_EXCEPTION,
        screenName: SCREEN_NAMES.REGISTRATION,
      });
    } else if (result?.data?.activationStatus === true) {
      logging?.trace({
        additionalDetails: getAnalyticsBaseConfig(),
        message: ANALYTICS_MESSAGES.REG_ACCOUNTSETUP_SUCCESS,
        screenName: SCREEN_NAMES.REGISTRATION,
      });
      if (!guest) {
        setEncryptedValue(UserDetails.registerId, result?.data?.memberId);
      }
      if (
        guest ||
        childOnlyPlanCheck() ||
        getFeature?.MpcInRegistration === false
      ) {
        setPage((currPage: number) => currPage + 2);
        setLoader(false);
      } else {
        setPage((currPage: number) => currPage + 1);
        setLoader(false);
      }
    } else if (result?.error) {
      const apiDetails = {
        exception: {
          errorType: getErrorTypeByCode(result?.errorCode?.toString()),
          message: result?.errorDetails,
          stack: result?.error,
        },
        response: { httpStatus: result?.errorCode },
      };
      logging?.error({
        additionalDetails: getAnalyticsBaseConfig({
          userId: '',
        }),
        apiDetails: apiDetails,
        message: ANALYTICS_MESSAGES.REG_ACCOUNTSETUP_EXCEPTION,
        screenName: SCREEN_NAMES.REGISTRATION,
      });
    }
  };

  /** Show checkbox error when submit is blocked by field validation */
  const onInvalid = () => {
    if (!checkBox) {
      setCheckBoxError(true);
    }
  };

  /**API Call to validate the Member register*/
  const onSubmit = async (data: AccountSetupData) => {
    if (!checkBox) {
      setCheckBoxError(true);
      return;
    }

    const { firstName, lastName, dob } =
      formData[FormTitles.indexOf('Personal Information')];
    const emailID = getValues('emailID');
    const name = getValues('name');
    const password = getValues('password');
    const ssn = formData[FormTitles.indexOf('Personal Information')].ssn
      ? formData[FormTitles.indexOf('Personal Information')].ssn
      : formData[FormTitles.indexOf('Personal Information')].guest;

    if (!firstName || !lastName || !emailID || !name || !password || !dob) {
      return;
    }

    setLoader(true);
    const isValid = await usernameValidation(data);
    if (!isValid) {
      setLoader(false);
      return;
    }

    let result: any;
    const personalData = buildPersonalData(
      firstName,
      lastName,
      emailID,
      dob,
      ssn,
      name,
      password
    );
    // else block for guest and non-guest registration
    if (guest) {
      result = await GuestRegister(personalData);
    } else {
      const planId = getPlanId();
      const memberIdData = buildMemberIdData();
      const payload = { ...personalData, ...memberIdData, planId };
      result = await ProfileRegister(payload);
      setEncryptedValue(UserDetails.registerId, result?.data?.memberId);
    }

    handleApiResult(result);
  };

  //Display the errror message based on the error code
  const globalError = [
    {
      condition: errorMessage,
      message: 'activation.userName',
      errorCode: errorCode,
    },
    {
      condition: errorMessage1,
      message: 'activation.userNameErrorMsg1',
      errorCode: errorCode,
    },
    {
      condition: errorPassword,
      message: 'common.passwordrequired',
      errorCode: errorCode,
    },
  ];

  /** API call to validate the username criteria*/
  const usernameValidation = async (data: AccountSetupData) => {
    setFormData({ ...formData, [page]: { ...data } });
    const response = await RegisterUserName(data?.name);
    if (response?.errorCode) {
      logging?.error({
        message: ANALYTICS_MESSAGES.REG_ACCOUNTSETUP_EXCEPTION,
        screenName: SCREEN_NAMES.REGISTRATION,
        additionalDetails: {
          ...getAnalyticsBaseConfig({ userId: '' }),
          step: page,
        },
        apiDetails: {
          exception: {
            errorType: getErrorTypeByCode(response?.errorCode?.toString()),
            message: response?.errorDetails?.detail,
            stack: response?.error,
          },
          response: { httpStatus: response?.errorCode },
        },
      });
      errorHandle(response?.errorCode);
      formSubmissionFailed({
        segment: `${domainForSegment}${pathname}`,
        form: {
          ...AdobeFormConfig['accountSetupUsernameError'],
          formFailReason: 'Requested Username already exists',
        },
      });
      return false;
    } else {
      setErrorMessage(false);
      setErrorMessage1(false);
      const registerUser_Response = await RegisterUserNameValidation(data.name);
      if (registerUser_Response?.errorCode) {
        logging?.error({
          message: ANALYTICS_MESSAGES.REG_ACCOUNTSETUP_EXCEPTION,
          screenName: SCREEN_NAMES.REGISTRATION,
          additionalDetails: {
            ...getAnalyticsBaseConfig(),
            step: page,
          },
          apiDetails: {
            exception: {
              errorType: getErrorTypeByCode(
                registerUser_Response?.errorCode?.toString()
              ),
              message: registerUser_Response?.errorDetails?.detail,
              stack: registerUser_Response?.error,
            },
            response: { httpStatus: registerUser_Response?.errorCode },
          },
        });
        errorHandle(registerUser_Response?.errorCode);
        formSubmissionFailed({
          segment: `${domainForSegment}${pathname}`,
          form: {
            ...AdobeFormConfig['accountSetupUsernameError'],
            formFailReason: t('activation.userNameErrorMsg1'),
          },
        });
        return false;
      } else {
        return await userPasswordValidation();
      }
    }
  };

  /** Error Handling to diplay the error message when username entered wrong*/
  const errorHandle = (errorCode: string): void => {
    const checkValidUser = ['441', '400', '442', '445'];
    const checkUserExist = ['449', '439', '409'];
    const isValidUser: boolean = checkValidUser.includes(errorCode.toString());
    const isUserExist: boolean = checkUserExist.includes(errorCode.toString());
    if (isValidUser) {
      setErrorMessage(true);
      setErrorMessage1(false);
    } else if (isUserExist) {
      setErrorMessage(false);
      setErrorMessage1(true);
    } else {
      setErrorMessage(false);
      setErrorMessage1(false);
    }
    setLoader(false);
  };

  /** API call to validate the password criteria*/
  const userPasswordValidation = async () => {
    const data: any = {
      firstName:
        formData[FormTitles.indexOf('Personal Information')]?.firstName,
      lastName: formData[FormTitles.indexOf('Personal Information')]?.lastName,
      passwordNew: getValues('password'),
      userId: getValues('name'),
    };

    const response = await userPassword(data);
    if (response?.errorCode) {
      logging?.error({
        message: ANALYTICS_MESSAGES.REG_ACCOUNTSETUP_EXCEPTION,
        screenName: SCREEN_NAMES.REGISTRATION,
        additionalDetails: {
          ...getAnalyticsBaseConfig({ userId: '' }),
          step: page,
        },
        apiDetails: {
          exception: {
            errorType: getErrorTypeByCode(response?.errorCode?.toString()),
            message: response?.errorDetails?.detail,
            stack: response?.error,
          },
          response: { httpStatus: response?.errorCode },
        },
      });
      formSubmissionFailed({
        segment: `${domainForSegment}${pathname}`,
        form: {
          ...AdobeFormConfig['accountSetupPasswordError'],
          formFailReason: response?.error ?? 'unknown',
        },
      });
      handlePasswordError(response?.errorCode);
      return false;
    } else {
      logging?.trace({
        additionalDetails: getAnalyticsBaseConfig(),
        message: ANALYTICS_MESSAGES.REG_ACCOUNTSETUP_SUCCESS,
        screenName: SCREEN_NAMES.REGISTRATION,
      });
      formSubmissionSucceeded({
        segment: `${domainForSegment}${pathname}`,
        form: AdobeFormConfig['accountSetupSuccess'],
      });
      setErrorPassword(false);
      return true;
    }
  };

  /** API call to validate the password criteria*/
  const handlePasswordError = (errorCode: string): void => {
    const errorStatusCode: string[] = [
      '441',
      '400',
      '442',
      '445',
      '439',
      '443',
      '448',
      '422',
    ];
    const checkError = errorStatusCode.includes(errorCode.toString());
    if (checkError) {
      setErrorPassword(true);
    } else {
      setErrorPassword(false);
    }
  };

  // Handle key down event
  const handlePasswordKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.code === 'Space') {
      e.preventDefault();
    }
  };

  // Set the strength bar color based on password strength
  const inputStrengthClass =
    pwdStrength in PASSWORD_CRITERIA
      ? `strength-bar-${PASSWORD_CRITERIA[pwdStrength]}`
      : '';

  return (
    <>
      <FormProvider {...methods}>
        <form
          data-testid="submit_form"
          autoComplete="off"
          onSubmit={handleSubmit(onSubmit, onInvalid)}
        >
          {loader && <Loader />}
          <div className="row">
            <div className="col-lg-6 col-md-8 col-12 mt-n2" aria-live="polite">
              {globalError.map(
                (error, index) =>
                  error.condition && (
                    <GlobalError
                      key={index}
                      variant="mb-4"
                      dataTestId="userNameInputError"
                    >
                      {t(error.message)}
                    </GlobalError>
                  )
              )}
              {FormTitles[page] !== '' && (
                <>
                  <span className="visually-hidden me-1">
                    {t('accessibility.message.registrationCurrentPage')}{' '}
                    {[page + 1]}
                  </span>
                  <Headings
                    level={1}
                    text={t(FormTitleTranslationKeys[FormTitles[page]])}
                    variant="semiBold"
                    dataTestId="registrationHeading"
                    className="mb-4 pb-4"
                  />
                </>
              )}
              <Paragraph
                level={5}
                text={t('activation.createUserPswd')}
                variant="medium"
                className="my-4"
              />
              <div className="mb-4 position-relative">
                <Label
                  className="pb-1"
                  dataTestId={'userNameInputLabel'}
                  htmlFor="regUserName"
                >
                  {t('common.userName')}
                </Label>

                <div className="d-flex align-items-center">
                  <div className="position-relative flex-1">
                    <InputField
                      id="regUserName"
                      data-testid="userNameInput"
                      placeholder={t('activation.userNameSmallPlaceholder')}
                      maxLength={getMaxLength.userName}
                      className={`form-input ${
                        errors?.name ? 'error-border' : ''
                      }`}
                      {...register('name', { required: true })}
                      onKeyDown={handleKeyDownUsername}
                      aria-describedby="regUserName_error"
                    />
                    {errors?.name?.type === 'required' && (
                      <div className="mt-1">
                        <ErrorMessage data-testid={'confirmEmailInputError'}>
                          {errorUserName}
                        </ErrorMessage>
                      </div>
                    )}
                    {errors?.name?.type === 'validate' && (
                      <div className="mt-1">
                        <ErrorMessage data-testid={'confirmEmailInputError'}>
                          {t('activation.firstCharUserName')}{' '}
                        </ErrorMessage>
                      </div>
                    )}
                  </div>

                  <div className={`${errors?.name ? 'mt-n3' : ''} ms-2`}>
                    <Button
                      variant="icon_btn"
                      data-testid="userNameInputHelpIcon"
                      onClick={openModal}
                    >
                      <HelpIcon />
                    </Button>
                  </div>
                </div>
              </div>
              <div className="pb-4">
                <div className="d-flex align-items-center">
                  <div className="position-relative flex-1">
                    <ChangePassword
                      onKeyDown={handlePasswordKeyDown}
                      setValue={setValue}
                      showPasswordPlaceholder={`${t(
                        'common.passwordPlaceholder'
                      )}`}
                    />
                  </div>
                </div>
              </div>
              {/* Render PasswordStrengthMeter component */}
              <div className="pt-1 pb-2">
                <PasswordStrengthMeter
                  inputStrengthClass={inputStrengthClass}
                />
              </div>
              <div className="mb-4 position-relative">
                <ConfirmPassword
                  onKeyDown={handlePasswordKeyDown}
                  showPasswordPlaceholder={`${t('activation.confirmPasswordPlaceholder')}`}
                />
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-xl-6 col-lg-6 col-md-8 col-12">
              <hr />
              <Paragraph
                level={5}
                text={t('activation.verifyEmail')}
                variant="medium"
                className="my-4"
              />
              <div className="mb-4 position-relative">
                <Label
                  dataTestId="emailInputLabel"
                  htmlFor="regEmailAddress"
                  className="pb-1"
                >
                  {t('common.email')}
                </Label>
                <InputField
                  id="regEmailAddress"
                  maxLength={getMaxLength.email}
                  className={`${
                    errors?.emailID?.type === 'required' ? 'error-border' : ''
                  }`}
                  placeholder={t('common.emaillblPlaceholder')}
                  data-testid="emailInput"
                  {...register('emailID', {
                    required: true,
                    pattern: {
                      value: EmailRegex,
                      message: t('activation.passwordCriteriaNotMatch'),
                    },
                  })}
                  aria-describedby="emailAddress_error"
                />
                {errors?.emailID && (
                  <div className="mt-1">
                    <ErrorMessage data-testid={'emailUpdate'}>
                      {t('common.emailIdValid')}
                    </ErrorMessage>
                  </div>
                )}
              </div>
              <div className="mb-4 position-relative">
                <Label
                  dataTestId="confirmEmailInputLabel"
                  htmlFor="confirmEmailAddress"
                  className="pb-1"
                >
                  {t('activation.confirmEmailLabel')}
                </Label>

                <InputField
                  maxLength={getMaxLength.email}
                  className={`${errors.confirmEmailID ? 'error-border' : ''}`}
                  placeholder={t('activation.confirmEmailLabel')}
                  id="confirmEmailAddress"
                  data-testid="confirmEmailInput"
                  {...register('confirmEmailID', {
                    required: true,
                    validate: (value) => {
                      return (
                        value.toLowerCase() ===
                        getValues('emailID').toLowerCase()
                      );
                    },
                  })}
                  aria-describedby="confirmEmail_error"
                />
                {errors.confirmEmailID && (
                  <div className="mt-1">
                    <ErrorMessage data-testid={'confirmEmailInputError'}>
                      {t('activation.confirmEmail')}
                    </ErrorMessage>
                  </div>
                )}
              </div>
              <div
                className={`d-flex position-relative ${
                  checkBoxError ? 'mb-1' : 'mb-4'
                }`}
              >
                <Checkbox
                  id="termAndConditioncheck"
                  className={checkBoxError ? 'mt-0 error-input-bdr' : 'mt-0'}
                  type="checkbox"
                  dataTestId="termsandconditionCheckbox"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    checkboxValid(e)
                  }
                  aria-describedby="termsofuse_error"
                >
                  {t('activation.accept')}{' '}
                  <Link
                    to={ssoEndPoints?.termAndCondition}
                    target="_blank"
                    rel="noreferrer"
                    className="text-decoration-underline small-link"
                    data-testid="termsandconditionLink"
                  >
                    {t('common.termsOfUse')}
                  </Link>
                </Checkbox>
              </div>
              {checkBoxError && (
                <div className="col-12">
                  <ErrorMessage>
                    {t('activation.acceptTermsAndCondition')}
                  </ErrorMessage>
                </div>
              )}
            </div>
          </div>
          <div className="row pt-4 pb-2">
            <div className="col-lg-auto col-md-auto col-12 d-grid gap-2 d-md-block mb-4">
              <Button
                dataTestId={'continueButton'}
                variant="primary_btn"
                className={`btn-md-width ${!isValid ? 'disabled' : ''}`}
                disabled={loader}
                type="submit"
              >
                {t('common.continue')}
              </Button>
            </div>
            <div className="col-lg-auto col-md-auto col-12 d-grid gap-2 d-md-block mb-4">
              <Button
                dataTestId={'cancelButton'}
                variant="secondary_btn"
                className="btn-md-width"
                type="button"
                onClick={(e: React.MouseEvent<HTMLElement>) => {
                  e.preventDefault();
                  logging?.trace?.({
                    message: ANALYTICS_MESSAGES.REG_ACCOUNTSETUP_CANCEL,
                    screenName: SCREEN_NAMES.REGISTRATION,
                    additionalDetails: {
                      ...getAnalyticsBaseConfig(),
                      step: page,
                      reason: ANALYTICS_MESSAGES.USER_CLICKED_CANCEL,
                    },
                  });
                  setFormData({
                    ...formData,
                    ...{
                      [page]: formData[page]
                        ? Object.keys(formData[page]).reduce((obj, item) => {
                            obj = { ...obj, [item]: '' };
                            return obj;
                          }, {})
                        : {},
                    },
                  });
                  setPage((currPage: number) => currPage - 1);
                  setLoader(false);
                  formSubmissionFailed({
                    segment: `${domainForSegment}${pathname}`,
                    form: AdobeFormConfig['accountSetupCancel'],
                  });
                }}
              >
                {t('common.cancel')}
              </Button>
            </div>
          </div>
        </form>
      </FormProvider>
      <Modal
        isOpen={isOpen}
        onRequestClose={closeModal}
        data-testid="userNameInputModal"
        contentLabel="Username Information Overlay"
        style={ModalCustomStyle}
      >
        <div className="w-member-overlay" data-testid="userNameInputModal">
          <div className="d-flex justify-content-end mb-2">
            <Button
              type="button"
              data-testid="userNameInputModalClose"
              variant="icon_btn"
              onClick={closeModal}
              aria-label="Close"
            >
              <CloseIconBlock />
            </Button>
          </div>
          <UsernameInfoOverlay />
        </div>
      </Modal>
      <Modal
        isOpen={isPasswordModalOpen}
        onRequestClose={closeModalPassword}
        data-userid="passwordInputModal"
        contentLabel="Password Information Overlay"
        style={ModalCustomStyle}
      >
        <div className="w-member-overlay" data-testid="passwordInputModalModal">
          <div className="d-flex justify-content-end mb-2">
            <Button
              type="button"
              data-testid="passwordInputModalClose"
              iconLeft={<CloseIconBlock />}
              variant="icon_btn"
              onClick={closeModalPassword}
              aria-label="Close"
            >
              {t('appointments.buttonLableTwoClose')}
            </Button>
          </div>
          <PasswordInfoOverlay />
        </div>
      </Modal>
    </>
  );
};

export default AccountSetup;